import { createContext, useContext, useState, ReactNode } from "react";
import { AttentionMetric, AttentionSubMetrics } from "@/api/studio/insights/types/metrics.type";

/* Types */
export type ReportType = "creative" | "attention" | "emotion" | "memory";
export type EmotionMetric = "visuals" | "text";
export type MemoryMetric = "branding" | "visuals" | "text";
export type ViewType = "Cards" | "List" | "Chart";

export interface ReportsState {
  assetKey: string;
  contextKey: string;
  reportType: ReportType;
  attentionMetric: AttentionMetric;
  attentionSubMetric: AttentionSubMetrics;
  emotionMetric: EmotionMetric;
  memoryMetric: MemoryMetric;
  view: ViewType;
  showCopilot: boolean;
}

export interface ReportsActions {
  setAssetKey: (assetKey: string) => void;
  setContextKey: (contextKey: string) => void;
  setReportType: (reportType: ReportType) => void;
  setAttentionMetric: (metric: AttentionMetric) => void;
  setAttentionSubMetric: (subMetric: AttentionSubMetrics) => void;
  setEmotionMetric: (metric: EmotionMetric) => void;
  setMemoryMetric: (metric: MemoryMetric) => void;
  setView: (view: ViewType) => void;
  setShowCopilot: (showCopilot: boolean) => void;
  resetToDefaults: () => void;
}

export interface ReportsContextValue {
  state: ReportsState;
  actions: ReportsActions;
}

/* Default State */
const defaultState: ReportsState = {
  assetKey: "",
  contextKey: "",
  reportType: "creative",
  attentionMetric: "clarity",
  attentionSubMetric: "pop",
  emotionMetric: "visuals",
  memoryMetric: "branding",
  view: "Cards",
  showCopilot: false,
};

/* Context */
const ReportsContext = createContext<ReportsContextValue | undefined>(undefined);

/* Provider Props */
interface ReportsProviderProps {
  children: ReactNode;
}

/* Provider Component */
export const ReportsProvider = ({ children }: ReportsProviderProps) => {
  const [state, setState] = useState<ReportsState>(defaultState);

  const actions: ReportsActions = {
    setAssetKey: (assetKey: string) => {
      setState((prev) => ({ ...prev, assetKey }));
    },

    setContextKey: (contextKey: string) => {
      setState((prev) => ({ ...prev, contextKey }));
    },

    setReportType: (reportType: ReportType) => {
      setState((prev) => ({ ...prev, reportType }));
    },

    setAttentionMetric: (attentionMetric: AttentionMetric) => {
      setState((prev) => ({ ...prev, attentionMetric }));
    },

    setAttentionSubMetric: (attentionSubMetric: AttentionSubMetrics) => {
      setState((prev) => ({ ...prev, attentionSubMetric }));
    },

    setEmotionMetric: (emotionMetric: EmotionMetric) => {
      setState((prev) => ({ ...prev, emotionMetric }));
    },

    setMemoryMetric: (memoryMetric: MemoryMetric) => {
      setState((prev) => ({ ...prev, memoryMetric }));
    },

    setView: (view: ViewType) => {
      setState((prev) => ({ ...prev, view }));
    },

    setShowCopilot: (showCopilot: boolean) => {
      setState((prev) => ({ ...prev, showCopilot }));
    },

    resetToDefaults: () => {
      setState(defaultState);
    },
  };

  const contextValue: ReportsContextValue = {
    state,
    actions,
  };

  return <ReportsContext.Provider value={contextValue}>{children}</ReportsContext.Provider>;
};

/* Hook */
export const useReportsContext = (): ReportsContextValue => {
  const context = useContext(ReportsContext);

  if (!context) {
    throw new Error("useReportsContext must be used within a ReportsProvider");
  }

  return context;
};
